import Image from 'next/image';
import { Target, Eye } from 'lucide-react';
import { WhoWeAre } from '@/lib/aboutPage';

const WhoWeAreSection = () => {
  return (
    <section 
      id="mission-vision"
      className="py-16 px-6 bg-white"
      aria-labelledby="who-we-are-heading"
    >
      <div className="max-w-7xl mx-auto flex flex-col lg:flex-row items-center lg:items-stretch gap-10 lg:gap-20">
        
        {/* Visual Element (Now Visually Left) */}
        <div 
          className="relative w-full lg:w-1/2 min-h-[400px] lg:min-h-auto rounded-3xl overflow-hidden shadow-2xl order-2 lg:order-1"
          role="img"
          aria-label="A team visualizing business goals through strategic planning"
        >
          <Image
            src={WhoWeAre.image}
            alt="" 
            fill
            sizes="(max-width: 1024px) 100vw, 50vw"
            className="object-cover"
            loading="lazy"
          />
          {/* Adjusted gradient to flow from the left for desktop */}
          <div className="absolute inset-0 bg-gradient-to-t from-[#001D4A]/60 lg:bg-gradient-to-l lg:from-[#001D4A]/20 to-transparent" aria-hidden="true" />
          
          <aside className="absolute bottom-4 left-4 right-4 sm:bottom-8 sm:left-8 sm:right-8 bg-white/95 backdrop-blur-sm p-6 sm:p-8 rounded-2xl border-r-4 border-[#4F7AC2] shadow-xl">
            <blockquote className="m-0">
              <p className="text-[#001D4A] text-sm sm:text-base font-semibold italic leading-relaxed">
                &ldquo;Enabling our clients to focus on strategy while we execute with discipline.&rdquo;
              </p>
            </blockquote>
          </aside>
        </div>

        {/* Content Side (Now Visually Right) */}
        <div className="w-full lg:w-1/2 flex flex-col justify-center order-1 lg:order-2">
          <header className="text-left">
            <div className="flex items-center gap-3 text-[#4F7AC2] font-bold mb-4 sm:mb-6">
              <div className="h-[2px] w-8 bg-[#4F7AC2]" aria-hidden="true" />
              <span className="uppercase tracking-[0.2em] text-[10px] sm:text-xs">
                About Our Foundation
              </span>
            </div>

            <h2 
              id="who-we-are-heading"
              className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-[#001D4A] mb-6 sm:mb-8 leading-tight"
            >
              Building competitive <br className="hidden sm:block" /> business sectors
            </h2>
          </header>

          <div className="space-y-4 sm:space-y-6">
            {/* Vision Article */}
            <article className="group flex flex-col sm:flex-row gap-4 sm:gap-6 p-5 sm:p-6 rounded-2xl border border-slate-100 hover:border-[#4F7AC2]/30 hover:bg-slate-50 transition-all duration-300">
              <div 
                className="flex-shrink-0 w-12 h-12 sm:w-14 sm:h-14 rounded-xl bg-[#4F7AC2]/10 flex items-center justify-center text-[#4F7AC2] group-hover:bg-[#4F7AC2] group-hover:text-white transition-colors"
                aria-hidden="true"
              >
                <Eye className="w-6 h-6 sm:w-7 sm:h-7" />
              </div>
              <div>
                <h3 className="text-lg sm:text-xl font-bold text-[#001D4A] mb-2">
                  {WhoWeAre.visionTitle}
                </h3>
                <p className="text-sm sm:text-base text-gray-600 leading-relaxed">
                  {WhoWeAre.visionDescription}
                </p>
              </div>
            </article>

            {/* Mission Article */}
            <article className="group flex flex-col sm:flex-row gap-4 sm:gap-6 p-5 sm:p-6 rounded-2xl border border-slate-100 hover:border-[#4F7AC2]/30 hover:bg-slate-50 transition-all duration-300">
              <div 
                className="flex-shrink-0 w-12 h-12 sm:w-14 sm:h-14 rounded-xl bg-[#4F7AC2]/10 flex items-center justify-center text-[#4F7AC2] group-hover:bg-[#4F7AC2] group-hover:text-white transition-colors"
                aria-hidden="true"
              >
                <Target className="w-6 h-6 sm:w-7 sm:h-7" />
              </div>
              <div>
                <h3 className="text-lg sm:text-xl font-bold text-[#001D4A] mb-2">
                  {WhoWeAre.missionTitle}
                </h3>
                <p className="text-sm sm:text-base text-gray-600 leading-relaxed">
                  {WhoWeAre.missionDescription}
                </p>
              </div>
            </article>
          </div>
        </div>

      </div>
    </section>
  );
};

export default WhoWeAreSection;